import React, { createContext, useCallback, useEffect, useMemo, useState } from "react";
import { jwtDecode } from "jwt-decode";
import { fetchUserByUUID } from "../api/users";
import { PLATFORM_COMPANY_ID } from "../constants/companies";

export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setTokenState] = useState(() => localStorage.getItem("access_token"));
  const [userId, setUserId] = useState(null);
  const [company, setCompany] = useState(null);
  const [companyRole, setCompanyRole] = useState(null);

  const setToken = (newToken) => {
    localStorage.setItem("access_token", newToken);
    setTokenState(newToken);
  };

  const logout = useCallback(() => {
    localStorage.removeItem("access_token");
    setTokenState(null);
    setUserId(null);
    setCompany(null);
    setCompanyRole(null);
  }, []);

  useEffect(() => {
    if (!token) {
      setUserId(null);
      setCompany(null);
      setCompanyRole(null);
      return;
    }

    let isCancelled = false;
    let decoded;
    try {
      decoded = jwtDecode(token);
    } catch {
      logout();
      return;
    }
    const subject = decoded?.sub;
    if (!subject) {
      logout();
      return;
    }

    const needsProfile = !userId || userId !== subject;
    if (!needsProfile) {
      return;
    }

    const loadProfile = async () => {
      try {
        const { data } = await fetchUserByUUID(subject);
        if (isCancelled) return;
        setUserId(data.user_id);
        setCompany(data.company || null);
        setCompanyRole(data.company_role || null);
      } catch {
        if (!isCancelled) {
          logout();
        }
      }
    };

    loadProfile();
    return () => {
      isCancelled = true;
    };
  }, [token, userId, company, logout]);

  const value = useMemo(() => {
    const normalizedRole = (companyRole || "").toLowerCase();
    const isPlatformAdmin = Boolean(
      company &&
      company.company_id === PLATFORM_COMPANY_ID &&
      normalizedRole === "admin"
    );
    const trustedCompany = Boolean(
      company &&
      (company.trusted_for_certification || ["CERTIFICATION_ENTITY", "PLATFORM"].includes(company.company_type))
    );
    const canCertify = Boolean(
      company &&
      ["admin", "expert"].includes(normalizedRole) &&
      trustedCompany
    );

    const canManageCompanyUsers = Boolean(
      isPlatformAdmin ||
      (
        company &&
        ["CERTIFICATION_ENTITY", "INTERMEDIARY"].includes(company.company_type) &&
        normalizedRole === "admin"
      )
    );

    return {
      token,
      setToken,
      userId,
      setUserId,
      company,
      setCompany,
      companyRole,
      setCompanyRole,
      isPlatformAdmin,
      canCertify,
      canManageCompanyUsers,
      logout
    };
  }, [token, company, companyRole, userId]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
